/**
 * Validation utilities for form inputs
 */

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  // Comprehensive email regex pattern
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
  }

  // Additional checks
  if (email.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }

  const parts = email.split('@');
  if (parts[0].length > 64) {
    return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate phone number based on country code
 * @param {string} phone - Phone number to validate
 * @param {string} countryCode - Country dial code (e.g., '+1', '+44')
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validatePhone = (phone, countryCode = '+1') => {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Remove all non-numeric characters for validation
  const cleanPhone = phone.replace(/\D/g, '');

  if (!cleanPhone) {
    return { isValid: false, error: 'Phone number must contain digits' };
  }

  // Get country-specific validation rules
  const dialCode = countryCode.replace('+', '');
  const rules = getPhoneValidationRules(dialCode);

  // Check minimum length
  if (cleanPhone.length < rules.minLength) {
    return { 
      isValid: false, 
      error: `Please enter a valid phone number with ${rules.minLength} digits (e.g., ${countryCode} 123-456-7890)` 
    };
  }

  // Check maximum length
  if (cleanPhone.length > rules.maxLength) {
    return { 
      isValid: false, 
      error: `Phone number must not exceed ${rules.maxLength} digits (e.g., ${countryCode} 123-456-7890)` 
    };
  }

  // Check if it's all zeros or all same digit
  if (/^(\d)\1+$/.test(cleanPhone)) {
    return { isValid: false, error: `Please enter a valid phone number (e.g., ${countryCode} 123-456-7890)` };
  }

  return { isValid: true, error: '' };
};

/**
 * Get phone validation rules based on country dial code
 * @param {string} dialCode - Numeric dial code
 * @returns {Object} - { minLength, maxLength }
 */
const getPhoneValidationRules = (dialCode) => {
  const rules = {
    '1': { minLength: 10, maxLength: 10 },      // US, Canada
    '44': { minLength: 10, maxLength: 10 },     // UK
    '91': { minLength: 10, maxLength: 10 },     // India
    '92': { minLength: 10, maxLength: 10 },     // Pakistan
    '61': { minLength: 9, maxLength: 9 },       // Australia
    '86': { minLength: 11, maxLength: 11 },     // China
    '81': { minLength: 10, maxLength: 10 },     // Japan
    '49': { minLength: 10, maxLength: 11 },     // Germany
    '33': { minLength: 9, maxLength: 9 },       // France
    '39': { minLength: 9, maxLength: 10 },      // Italy
    '34': { minLength: 9, maxLength: 9 },       // Spain
    '7': { minLength: 10, maxLength: 10 },      // Russia
    '55': { minLength: 10, maxLength: 11 },     // Brazil
    '52': { minLength: 10, maxLength: 10 },     // Mexico
    '82': { minLength: 9, maxLength: 10 },      // South Korea
    '31': { minLength: 9, maxLength: 9 },       // Netherlands
    '46': { minLength: 9, maxLength: 10 },      // Sweden
    '41': { minLength: 9, maxLength: 9 },       // Switzerland
    '65': { minLength: 8, maxLength: 8 },       // Singapore
    '971': { minLength: 9, maxLength: 9 },      // UAE
    '966': { minLength: 9, maxLength: 9 },      // Saudi Arabia
    '27': { minLength: 9, maxLength: 9 },       // South Africa
    '234': { minLength: 10, maxLength: 10 },    // Nigeria
    '20': { minLength: 10, maxLength: 10 },     // Egypt
    '254': { minLength: 9, maxLength: 9 },      // Kenya
    '63': { minLength: 10, maxLength: 10 },     // Philippines
    '60': { minLength: 9, maxLength: 10 },      // Malaysia
    '66': { minLength: 9, maxLength: 9 },       // Thailand
    '84': { minLength: 9, maxLength: 10 },      // Vietnam
    '62': { minLength: 9, maxLength: 11 },      // Indonesia
  };

  // Return specific rules or default
  return rules[dialCode] || { minLength: 7, maxLength: 15 };
};

/**
 * Validate name field
 * @param {string} name - Name to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateName = (name) => {
  if (!name) {
    return { isValid: false, error: 'Name is required' };
  }

  if (name.trim().length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters' };
  }

  if (name.length > 100) {
    return { isValid: false, error: 'Name is too long' };
  }

  // Check if name contains only letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(name)) {
    return { isValid: false, error: 'Name can only contain letters, spaces, hyphens, and apostrophes' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate required text field
 * @param {string} text - Text to validate
 * @param {string} fieldName - Name of the field for error message
 * @param {number} minLength - Minimum length
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateRequired = (text, fieldName = 'This field', minLength = 1) => {
  if (!text || text.trim().length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (text.trim().length < minLength) {
    return { isValid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }

  return { isValid: true, error: '' };
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number to format
 * @param {string} countryCode - Country dial code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone, countryCode = '+1') => {
  const cleanPhone = phone.replace(/\D/g, '');
  const dialCode = countryCode.replace('+', '');

  // US/Canada formatting
  if (dialCode === '1' && cleanPhone.length === 10) {
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
  }

  // UK formatting
  if (dialCode === '44' && cleanPhone.length === 10) {
    return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(4, 7)} ${cleanPhone.slice(7)}`;
  }

  // Default: add spaces every 3-4 digits
  if (cleanPhone.length > 6) {
    return cleanPhone.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3');
  } else if (cleanPhone.length > 3) {
    return cleanPhone.replace(/(\d{3})(\d+)/, '$1 $2');
  }

  return cleanPhone;
};
